import { useEffect, useReducer, useRef } from 'react'
import { apiFetch, ApiError, writeCount } from '@/api/client'
import { useAuthContext } from '@/auth/AuthProvider'
import { useForcedSkeletons } from '@/api/skeletonDebug'

// ---------------------------------------------------------------------------
// Query state shape — same envelope every read hook returns.
// ---------------------------------------------------------------------------

export type QueryState<T> =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'success'; data: T }
  | { status: 'error'; error: ApiError | Error }

type QueryAction<T> =
  | { type: 'FETCH' }
  | { type: 'SUCCESS'; data: T }
  | { type: 'ERROR'; error: ApiError | Error }

function queryReducer<T>(state: QueryState<T>, action: QueryAction<T>): QueryState<T> {
  switch (action.type) {
    case 'FETCH':
      return { status: 'loading' }
    case 'SUCCESS':
      // The same object again (a cached result re-dispatched on mount) is the
      // same state, so React can skip the subtree.
      return state.status === 'success' && state.data === action.data
        ? state
        : { status: 'success', data: action.data }
    case 'ERROR':
      return { status: 'error', error: action.error }
    default:
      return state
  }
}

// ---------------------------------------------------------------------------
// Shared requests and recent results, across every hook instance
// ---------------------------------------------------------------------------

// Long enough to cover several components on one screen asking for the same
// path as they mount, short enough that navigating back refetches. A result
// older than this is still shown while the refetch runs, so a page the user
// has seen before opens with its last content instead of a skeleton.
const RESULT_TTL_MS = 5_000

// Results are kept per path for the session. Search and filter paths add up,
// so the oldest go once this many are held.
const RESULT_LIMIT = 200

interface Shared {
  promise: Promise<unknown>
  controller: AbortController
  subscribers: number
  /** writeCount() when the request started. */
  writes: number
}

const inFlight = new Map<string, Shared>()
const results = new Map<string, { data: unknown; at: number; writes: number }>()

function freshResult(path: string): { data: unknown } | null {
  const hit = results.get(path)
  if (!hit || hit.writes !== writeCount() || Date.now() - hit.at > RESULT_TTL_MS) return null
  return hit
}

/** The last result for `path`, however old, or null if the path was never fetched. */
function lastResult(path: string): { data: unknown } | null {
  return results.get(path) ?? null
}

function remember(path: string, data: unknown, writes: number): void {
  results.delete(path)
  results.set(path, { data, at: Date.now(), writes })
  if (results.size > RESULT_LIMIT) {
    const oldest = results.keys().next().value
    if (oldest !== undefined) results.delete(oldest)
  }
}

/**
 * Join the request in flight for `path`, or start one. `fresh` skips a
 * request started before now, for an explicit refresh.
 */
function subscribe(
  path: string,
  getHeaders: () => Promise<Record<string, string>>,
  fresh: boolean,
): Shared {
  const current = inFlight.get(path)
  if (current && !fresh && current.writes === writeCount()) {
    current.subscribers += 1
    return current
  }
  const controller = new AbortController()
  const writes = writeCount()
  const shared: Shared = {
    promise: apiFetch(path, getHeaders, { signal: controller.signal }),
    controller,
    subscribers: 1,
    writes,
  }
  inFlight.set(path, shared)
  shared.promise.then(
    data => {
      if (inFlight.get(path) === shared) inFlight.delete(path)
      remember(path, data, writes)
    },
    (err: unknown) => {
      if (inFlight.get(path) === shared) inFlight.delete(path)
      // Gone or refused for good: the last body must not greet the next mount.
      // Anything else (a 401 before the token refreshes, a 429, a server or
      // network failure) keeps it, since it is still the best thing to show
      // while the next try runs.
      if (err instanceof ApiError && [403, 404, 410].includes(err.status)) results.delete(path)
    },
  )
  return shared
}

/** Abort the underlying fetch only once nobody is waiting for it. */
function unsubscribe(path: string, shared: Shared): void {
  shared.subscribers -= 1
  if (shared.subscribers > 0) return
  if (inFlight.get(path) === shared) inFlight.delete(path)
  shared.controller.abort()
}

// ---------------------------------------------------------------------------
// useApiQuery — generic auth-aware GET hook
// ---------------------------------------------------------------------------

/**
 * Issue an authenticated GET against the API and track its lifecycle.
 *
 * `path` is the full API path including query string. The hook re-fires
 * whenever `path` changes — list views drive search/filter changes by
 * rebuilding the path string.
 *
 * `refreshKey` is an optional secondary trigger: any change to it
 * forces a re-fetch even when the path is the same. Use it after a
 * mutation when the data on the server has changed but the read URL
 * hasn't (e.g. PATCH and then re-fetch the same initiative). Defaults
 * to 0 so existing callers don't need to opt in.
 *
 * Identity headers are acquired per call via `getAuthHeaders()` (a token
 * refresh under Entra). Race condition: if two fetches are in flight
 * (e.g. user types fast), we discard results from anything other than
 * the most recent — there is no global request mutex; ordering is by
 * a per-mount sequence number.
 *
 * Hook instances asking for the same path share one request, and a result
 * stays reusable for RESULT_TTL_MS unless a write has completed since. A
 * request nobody is waiting for any more is aborted.
 *
 * Stale while revalidating: a path fetched earlier in the session starts in
 * `success` with its last result and refetches in the background, so only a
 * path never seen before shows `loading`. Callers that want to know a refetch
 * is running can compare `refreshKey`; the state itself stays `success`.
 */
const FORCED_LOADING: QueryState<never> = { status: 'loading' }

export function useApiQuery<T>(path: string, refreshKey: number = 0): QueryState<T> {
  const { getAuthHeaders } = useAuthContext()
  const forced = useForcedSkeletons()

  const [state, dispatch] = useReducer(
    queryReducer as (s: QueryState<T>, a: QueryAction<T>) => QueryState<T>,
    path,
    (p): QueryState<T> => {
      const last = lastResult(p)
      return last ? { status: 'success', data: last.data as T } : { status: 'idle' }
    },
  )

  // Capture the latest accessor without re-firing the effect every render.
  const getHeadersRef = useRef(getAuthHeaders)
  getHeadersRef.current = getAuthHeaders

  // Per-effect-run sequence number; result of an older fetch is discarded
  // if a newer fetch has already started.
  const seqRef = useRef(0)

  // The refreshKey of the previous run. A changed key means the caller knows
  // the server data changed, so neither a cached result nor a request already
  // in flight will do.
  const lastKeyRef = useRef(refreshKey)

  useEffect(() => {
    const seq = ++seqRef.current
    const refreshing = refreshKey !== lastKeyRef.current
    lastKeyRef.current = refreshKey

    const hit = refreshing ? null : freshResult(path)
    if (hit) {
      dispatch({ type: 'SUCCESS', data: hit.data as T })
      return
    }

    let cancelled = false
    // Something to show while the request runs: the last result for this
    // path, if there is one. Only a never-fetched path drops to loading.
    const last = lastResult(path)
    if (last) dispatch({ type: 'SUCCESS', data: last.data as T })
    else dispatch({ type: 'FETCH' })

    const shared = subscribe(path, () => getHeadersRef.current(), refreshing)
    shared.promise
      .then(data => {
        if (cancelled || seq !== seqRef.current) return
        dispatch({ type: 'SUCCESS', data: data as T })
      })
      .catch((err: unknown) => {
        if (cancelled || seq !== seqRef.current) return
        dispatch({
          type: 'ERROR',
          error: err instanceof Error ? err : new Error(String(err)),
        })
      })

    return () => {
      cancelled = true
      unsubscribe(path, shared)
    }
  }, [path, refreshKey])

  return forced ? FORCED_LOADING : state
}
